/**
 * Autosave queue for answers.
 *
 * The rule this exists to enforce: **an answer the student typed is never
 * lost.** Not to a dropped connection, not to a reload, not to a closed lid.
 *
 * How:
 *   - Every change is written to the queue synchronously and mirrored into
 *     localStorage before any request is made.
 *   - Entries are coalesced per question. The endpoint is an idempotent upsert
 *     of the whole response, so only the latest value wins and a retry is
 *     always safe.
 *   - A failed flush keeps the entry and retries with backoff. Nothing is
 *     dropped for failing.
 *   - On reload the mirror is restored and drains, so answers written during an
 *     outage survive a crash.
 *
 * `response` is ALWAYS the complete current answer, never a patch. That is not
 * incidental: `response: null` is how "clear response" is expressed, so an
 * entry that merely *defaulted* to null would silently wipe a saved answer.
 * Callers must pass what the student currently has selected.
 *
 * Time-on-question and visit offsets do NOT travel here — they go through
 * VisitQueue (`./visitQueue`) and `POST /api/attempts/:id/visits`, so that
 * merely opening a question can never rewrite its answer.
 */
import type { AnswerResponse, SaveAnswerInput } from "@exam/shared";
import { saveAnswer } from "@/lib/exam-api";
import { isApiError } from "@/lib/api";

const BACKOFF_MS = [500, 1_500, 4_000, 10_000, 20_000, 30_000];

function storageKey(attemptId: string): string {
  return `ep:answers:pending:${attemptId}`;
}

interface PendingEntry {
  question_id: string;
  /** The complete current answer. `null` means "cleared". */
  response: AnswerResponse | null;
  /** Undefined means "leave the stored flag alone". */
  marked_for_review?: boolean;
  /** Monotonic per queue; lets `settle` detect an edit made mid-request. */
  revision: number;
}

export interface AnswerQueueState {
  /** Questions with unsaved changes. */
  pending: number;
  saving: boolean;
  /** True once a flush has failed — drives the "retrying" indicator. */
  degraded: boolean;
  lastSavedAtMs: number | null;
  lastErrorMessage: string | null;
}

export interface AnswerQueueOptions {
  attemptId: string;
  onChange?: (state: AnswerQueueState) => void;
  /** Called when the server rejects a save because the attempt is over. */
  onAttemptClosed?: (message: string) => void;
}

export class AnswerQueue {
  private readonly attemptId: string;
  private readonly onChange?: (state: AnswerQueueState) => void;
  private readonly onAttemptClosed?: (message: string) => void;

  private entries = new Map<string, PendingEntry>();
  private timer: ReturnType<typeof setTimeout> | null = null;
  private flushing = false;
  private stopped = false;
  private failures = 0;
  private lastSavedAtMs: number | null = null;
  private lastErrorMessage: string | null = null;

  private readonly onOnline = () => {
    this.failures = 0;
    this.schedule(0);
  };

  constructor(options: AnswerQueueOptions) {
    this.attemptId = options.attemptId;
    this.onChange = options.onChange;
    this.onAttemptClosed = options.onAttemptClosed;
    this.restore();
    window.addEventListener("online", this.onOnline);
    if (this.entries.size > 0) this.schedule(0);
  }

  get state(): AnswerQueueState {
    return {
      pending: this.entries.size,
      saving: this.flushing,
      degraded: this.failures > 0,
      lastSavedAtMs: this.lastSavedAtMs,
      lastErrorMessage: this.lastErrorMessage,
    };
  }

  /** True while anything is unsaved — used to warn before leaving. */
  get hasPending(): boolean {
    return this.entries.size > 0;
  }

  /**
   * Questions with a local edit that has not been acknowledged yet. A poll must
   * not overwrite these, or the student's answer would visibly revert under
   * their cursor between typing it and it being saved.
   */
  pendingQuestionIds(): string[] {
    return [...this.entries.keys()];
  }

  /**
   * Queues a change and flushes immediately.
   *
   * `response` is the student's COMPLETE current answer for that question —
   * `null` clears it. There is no partial update.
   */
  enqueue(change: {
    questionId: string;
    response: AnswerResponse | null;
    markedForReview?: boolean;
  }): void {
    if (this.stopped) return;

    const existing = this.entries.get(change.questionId);

    this.entries.set(change.questionId, {
      question_id: change.questionId,
      response: change.response,
      marked_for_review: change.markedForReview ?? existing?.marked_for_review,
      revision: (existing?.revision ?? 0) + 1,
    });

    this.persist();
    this.emit();
    this.schedule(0);
  }

  /** Sends everything queued. Never rejects. */
  async flush(): Promise<void> {
    if (this.flushing || this.entries.size === 0 || this.stopped) return;

    this.flushing = true;
    this.emit();

    // Snapshot: a change made mid-flight must not be wiped by this pass.
    const batch = [...this.entries.values()];
    let failed = false;

    for (const entry of batch) {
      try {
        const body: SaveAnswerInput = {
          question_id: entry.question_id,
          response: entry.response,
          // Time and visits are VisitQueue's job — see the file header.
          time_spent_ms: 0,
          visit_offsets_ms: [],
          ...(entry.marked_for_review !== undefined
            ? { marked_for_review: entry.marked_for_review }
            : {}),
        };

        await saveAnswer(this.attemptId, body);

        // Subtract exactly what was sent rather than deleting the key — the
        // student may have changed this same answer while the request was in
        // flight, and that change must not be discarded.
        this.settle(entry);
        this.lastSavedAtMs = Date.now();
        this.failures = 0;
        this.lastErrorMessage = null;
      } catch (err) {
        if (isApiError(err, "conflict")) {
          // The attempt is over (submitted, or the server auto-submitted on
          // timeout). Retrying can never succeed, so stop and tell the UI.
          this.entries.delete(entry.question_id);
          this.persist();
          this.onAttemptClosed?.(err.message);
          continue;
        }

        failed = true;
        this.failures += 1;
        this.lastErrorMessage =
          err instanceof Error ? err.message : "Could not save";
        break;
      }
    }

    this.flushing = false;
    this.persist();
    this.emit();

    if (!this.stopped && this.entries.size > 0) {
      this.schedule(
        failed
          ? (BACKOFF_MS[Math.min(this.failures - 1, BACKOFF_MS.length - 1)] ??
            30_000)
          : 0,
      );
    }
  }

  /** Flush and stop accepting new work — call before submit. */
  async finish(): Promise<void> {
    if (this.timer) clearTimeout(this.timer);
    this.timer = null;
    await this.flush();
    this.stopped = true;
    window.removeEventListener("online", this.onOnline);
  }

  dispose(): void {
    this.stopped = true;
    if (this.timer) clearTimeout(this.timer);
    this.timer = null;
    window.removeEventListener("online", this.onOnline);
  }

  /* -- internals ---------------------------------------------------- */

  /**
   * Clears an entry once saved — unless the student changed that same answer
   * while the request was in flight, in which case the newer revision stays
   * queued and goes out on the next pass.
   */
  private settle(sent: PendingEntry): void {
    const current = this.entries.get(sent.question_id);
    if (!current) return;
    if (current.revision !== sent.revision) return;
    this.entries.delete(sent.question_id);
  }

  private schedule(ms: number): void {
    if (this.timer) clearTimeout(this.timer);
    this.timer = setTimeout(() => void this.flush(), ms);
  }

  private emit(): void {
    this.onChange?.(this.state);
  }

  private persist(): void {
    try {
      if (this.entries.size === 0) {
        localStorage.removeItem(storageKey(this.attemptId));
      } else {
        localStorage.setItem(
          storageKey(this.attemptId),
          JSON.stringify([...this.entries.values()]),
        );
      }
    } catch {
      /* quota or private mode — the in-memory queue still works */
    }
  }

  private restore(): void {
    try {
      const raw = localStorage.getItem(storageKey(this.attemptId));
      if (!raw) return;
      const parsed: unknown = JSON.parse(raw);
      if (!Array.isArray(parsed)) return;
      for (const entry of parsed as PendingEntry[]) {
        if (entry?.question_id) this.entries.set(entry.question_id, entry);
      }
    } catch {
      /* ignore corrupt state */
    }
  }
}
